import React, { useCallback } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  DatePickerV2,
  TextAreaV4,
  TextInputV4,
  labraUIDateFormat,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'
import { useStyles } from './OfferDetails.styles'
import { DateTime } from 'luxon'
import { MsftCombinedFormState } from '../../../../modules/microsoftCombinedForm/reducer'
import {
  CombinedFormFAQs,
  CombinedFormFAQsItem,
} from '../CombinedFormFAQs/CombinedFormFAQs'

export type OfferDetailsSectionProps = {
  disabled: boolean
}

export const OfferDetails: React.FC<OfferDetailsSectionProps> = ({
  disabled,
}) => {
  const formikProps = useFormikContext() as FormikProps<MsftCombinedFormState>
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    handleChange,
  } = formikProps

  const offerExpirationDateField = {
    title: 'Offer expiration date',
    humanReadableKey: 'Offer expiration date',
    placeHolder: 'Offer expiration date',
    id: 'draftOffer.acceptBy',
    isRequired: false,
    isReadOnly: false,
    isCustom: false,
    dataType: 'date',
    minDate: DateTime.now().plus({ days: 1 }).toISODate(),
  }

  const handleDateChange = useCallback(async (date: string | null) => {
    const updatedDate = date ? DateTime.fromISO(date).toISO() : ''
    await setFieldValue('draftOffer.acceptBy', updatedDate)
    await setFieldTouched('draftOffer.acceptBy', true)
  }, [])

  const { classes } = useStyles()
  return (
    <div data-testid="details-section">
      <BoxV4
        heading={'Offer details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <CombinedFormFAQs item={CombinedFormFAQsItem.OFFER_DETAIL} />
        }
      >
        <div className={classes.boxWrapper}>
          <div className={classes.sectionContainer}>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="draftOffer.companyName"
                  title="Buyer name"
                  type="text"
                  placeHolder=""
                  value={values.draftOffer.companyName as string}
                  updatedValue={values.draftOffer.companyName as string}
                  maxNumber={72}
                  isError={
                    !!(
                      get(touched, 'draftOffer.companyName', false) &&
                      get(errors, 'draftOffer.companyName', false)
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={disabled}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'draftOffer.companyName', false) &&
                      get(errors, 'draftOffer.companyName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>

              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="draftOffer.beneficiaryId"
                  title="Buyer billing account ID"
                  type="text"
                  placeHolder=""
                  value={(values.draftOffer.beneficiaryId || '') as string}
                  updatedValue={
                    (values.draftOffer.beneficiaryId || '') as string
                  }
                  isError={
                    !!(
                      get(touched, 'draftOffer.beneficiaryId', false) &&
                      get(errors, 'draftOffer.beneficiaryId', false)
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={disabled}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'draftOffer.beneficiaryId', false) &&
                      get(errors, 'draftOffer.beneficiaryId', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
          <div className={classes.sectionContainer}>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="draftOffer.mppoName"
                  title="Offer name"
                  type="text"
                  placeHolder=""
                  value={values.draftOffer.mppoName}
                  updatedValue={values.draftOffer.mppoName}
                  maxNumber={72}
                  isError={
                    !!(
                      get(touched, 'draftOffer.mppoName', false) &&
                      get(errors, 'draftOffer.mppoName', false)
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={disabled}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'draftOffer.mppoName', false) &&
                      get(errors, 'draftOffer.mppoName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>

              <div className={classes.inputWidth}>
                <DatePickerV2
                  format={labraUIDateFormat}
                  field={{
                    ...offerExpirationDateField,
                    defaultValue: values?.draftOffer.acceptBy as string,
                  }}
                  key={'draftOffer.acceptBy'}
                  showErrorHelper={
                    !!get(touched, 'draftOffer.acceptBy', false) &&
                    !isEmpty(get(errors, 'draftOffer.acceptBy', false))
                  }
                  fieldId={'draftOffer.acceptBy'}
                  errorHelperText={
                    (!!get(touched, 'draftOffer.acceptBy', false) &&
                      get(errors, 'draftOffer.acceptBy', ' ')) ||
                    ' '
                  }
                  handleDateChange={handleDateChange}
                  views={['year', 'month', 'day']}
                  size="small"
                />
              </div>
            </div>
          </div>

          <div className={classes.sectionContainer}>
            <TextAreaV4
              keyName="draftOffer.mppoDescription"
              title="Offer description (Optional)"
              rows={10}
              maxNumber={1000}
              height={'66px'}
              type="string"
              placeHolder="Offer description (Optional)"
              value={(values.draftOffer.mppoDescription || '') as string}
              updatedValue={(values.draftOffer.mppoDescription || '') as string}
              isError={
                !!(
                  get(touched, 'draftOffer.mppoDescription', false) &&
                  get(errors, 'draftOffer.mppoDescription', false)
                )
              }
              onChange={handleChange}
              isReadOnly={disabled}
              isRequired={false}
              errorText={
                (!!get(touched, 'draftOffer.mppoDescription', false) &&
                  get(errors, 'draftOffer.mppoDescription', ' ')) ||
                ' '
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
          <div className={classes.inputWidth}>
            <TextInputV4
              keyName="draftOffer.preparedBy"
              title="Prepared by email"
              type="text"
              placeHolder=""
              value={values.draftOffer.preparedBy as string}
              updatedValue={values.draftOffer.preparedBy as string}
              isError={
                !!(
                  get(touched, 'draftOffer.preparedBy', false) &&
                  get(errors, 'draftOffer.preparedBy', false)
                )
              }
              onChange={handleChange}
              isReadOnly={disabled}
              isRequired={false}
              errorText={
                (!!get(touched, 'draftOffer.preparedBy', false) &&
                  get(errors, 'draftOffer.preparedBy', ' ')) ||
                ' '
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
        </div>
      </BoxV4>
    </div>
  )
}
